import { useEffect, useState } from "react";
import { Rocket } from "lucide-react";

import { api } from "@/lib/api";
import { eurosFromCents, mapCatalogToCards } from "@/lib/landingCatalog";

// M4.3 (Juni 2026) – Preissektion (rein informativ). Daten AUSSCHLIESSLICH aus der
// öffentlichen Catalog-API (GET /api/catalog), KEINE Hardcodes, KEIN Checkout, KEINE
// Stripe-Logik. Der Exposure-Guard liegt serverseitig: solange keine Katalogversion
// public_visible ist, liefert die API {published:false} → Pre-Launch-Zustand ohne
// Preise (kein Fallback auf alte Werte). Die Test-CTAs rufen ausschließlich den
// bestehenden Demo-Handler (Prop onTest = startDemo).

export default function PricingSection({ onTest }) {
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    api
      .getPublicCatalog()
      .then((d) => {
        if (active) setCatalog(d);
      })
      .catch(() => {
        // Kein Fallback auf alte Preise: bei Fehler bleibt es beim Pre-Launch-Zustand.
        if (active) setCatalog({ published: false, version: null, products: [] });
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const { published, cards: allCards } = mapCatalogToCards(catalog);
  // PRICE-03b-2 (Juni 2026): nur monatliche Darstellung (kein Umschalter, kein
  // Jahrespreis). Produkte ohne Monatspreis (z. B. Einmalkauf) gehören nicht in
  // das monatliche Tarifraster; Enterprise ("Individuelles Angebot") bleibt.
  const cards = allCards.filter((t) => t.enterprise || t.monthly_cents != null);

  return (
    <section className="wrap lp-pricing" id="preise" data-testid="lp-pricing-section">
      <div className="lp-pricing-head">
        <h2>Ein passender Tarif für jeden Betrieb.</h2>
        <p>
          14 Tage kostenlos testen. Keine automatische Verlängerung. Alle Preise netto
          zzgl. gesetzlicher Umsatzsteuer.
        </p>
      </div>

      {loading ? (
        <div className="lp-price-toggle-wrap" data-testid="lp-pricing-loading">
          <p className="lp-price-soon">Lade Tarife…</p>
        </div>
      ) : !published ? (
        // Pre-Launch: Sektion + Anker #preise bleiben erhalten, KEINE Preise sichtbar,
        // neutrale Meldung + Test-CTA bleiben sichtbar.
        <div className="lp-price-toggle-wrap" data-testid="lp-pricing-prelaunch">
          <p
            data-testid="lp-pricing-prelaunch-msg"
            style={{
              textAlign: "center",
              color: "var(--muted)",
              fontSize: "1.05rem",
              margin: "0 auto 22px",
              maxWidth: 520,
            }}
          >
            Preise in Kürze verfügbar.
          </p>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={onTest}
              data-testid="lp-pricing-cta-prelaunch"
            >
              <Rocket size={16} /> 14 Tage kostenlos testen
            </button>
          </div>
        </div>
      ) : (
        <div className="lp-price-grid">
            {cards.map((t) => (
              <div className="lp-price-card" key={t.id} data-testid={`lp-pricing-card-${t.id}`}>
                <div className="lp-price-name">{t.name}</div>
                <div className="lp-price-seats">{t.seats}</div>

                {t.enterprise ? (
                  <div className="lp-price-amount indiv" data-testid={`lp-pricing-price-${t.id}`}>
                    Individuelles Angebot
                  </div>
                ) : (
                  <div className="lp-price-amount" data-testid={`lp-pricing-price-${t.id}`}>
                    <span className="num">{eurosFromCents(t.monthly_cents)} €</span>
                    <span className="per">/Monat</span>
                  </div>
                )}

                <div className="lp-price-benefit">{t.benefit}</div>

                <div className="lp-price-cta">
                  {t.enterprise ? (
                    <button
                      type="button"
                      className="btn btn-ghost"
                      disabled
                      data-testid="lp-pricing-cta-enterprise"
                    >
                      Angebot anfragen
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={onTest}
                      data-testid={`lp-pricing-cta-${t.id}`}
                    >
                      <Rocket size={16} /> 14 Tage kostenlos testen
                    </button>
                  )}
                </div>

                {t.enterprise && (
                  <div className="lp-price-soon" data-testid="lp-pricing-enterprise-soon">
                    Demnächst verfügbar
                  </div>
                )}

                <div className="lp-price-vat">
                  Alle Preise netto zzgl. gesetzlicher Umsatzsteuer.
                </div>
              </div>
            ))}
          </div>
      )}
    </section>
  );
}
